import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../../css/Containers/ContainerStyles.css";
import SearchBoxContainer from "../SearchBoxContainer";
import { Icon } from "@iconify/react";
import { Colors } from "../../../utils/colors";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { useLoadUserFollowList } from "../../../hooks/useLoadUserFollowList";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { AccountData, FollowerStatus } from "@markit/common.types";
import filter from "lodash.filter";
import ProfileItem from "../../ProfileItem";
import { formatPhoneNumber } from "react-phone-number-input";

type SearchBoxContactsDropdownProps = {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  contactOnPress: (contact: AccountData) => void;
};

// This search box component is used to search your contacts which are shown in a dropdown
const SearchBoxContactsDropdown = (props: SearchBoxContactsDropdownProps) => {
  const { searchTerm, setSearchTerm, contactOnPress } = props;
  const { uid } = useSelector(getAccountState).account.accountData;
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownVisible) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const target = event.target as Node | null;
      if (target && !el.contains(target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [dropdownVisible]);

  const { fetchedUserData, loadSearchResultsThrottled } = useLoadUserFollowList(
    {
      userId: uid,
      followListType: "Followers",
      followerStatus: FollowerStatus.SUBSCRIBED,
      windowSize: 5,
    }
  );

  const handleSearch = (text: string) => {
    const lowercaseText = text.toLowerCase();
    if (lowercaseText !== "") {
      loadSearchResultsThrottled(lowercaseText);
    }
    setDropdownVisible(text.length > 1);
    setSearchTerm(lowercaseText);
  };

  const containsUser = useCallback((item: AccountData, query: string) => {
    return (
      item.fullName.toLowerCase().includes(query) && item.phoneNumber !== ""
    );
  }, []);

  const contactsToShow: AccountData[] = useMemo(() => {
    if (searchTerm === "") {
      return [];
    }
    let userList: AccountData[] = fetchedUserData;
    userList = filter(userList, (user: AccountData) => {
      return containsUser(user, searchTerm);
    });
    return userList.slice(0, 5);
  }, [searchTerm, fetchedUserData, containsUser]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <SearchBoxContainer
        placeholder="Search by name..."
        value={searchTerm}
        autoComplete="off"
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => setDropdownVisible(searchTerm.length > 1)}
        containerStyles={{ marginTop: 0, backgroundColor: Colors.WHITE1 }}
      />
      {dropdownVisible ? (
        <div
          className="ColumnNormal"
          style={{
            position: "absolute",
            zIndex: 99,
            top: 50,
            left: 0,
            right: 0,
            width: "100%",
            boxSizing: "border-box",
            padding: 10,
            borderRadius: 8,
            boxShadow: "0px 1px 6px 0px #b9b9b9",
            backgroundColor: Colors.WHITE,
          }}
        >
          {contactsToShow.length > 0 ? (
            contactsToShow.map((contact) => {
              return (
                <div
                  onClick={() => {
                    contactOnPress(contact);
                    setDropdownVisible(false);
                  }}
                  className="AlignedRowSpacedSelect ConversationSidebarItem"
                  style={{ borderRadius: 8, paddingInline: 7 }}
                  key={contact.uid}
                >
                  <ProfileItem
                    profileItem={contact}
                    subtext={
                      <span
                        className="smallBodySubtext"
                        style={{ marginTop: 2 }}
                      >
                        {formatPhoneNumber(contact.phoneNumber)}
                      </span>
                    }
                    hideViewIcon
                    onItemPress={() => contactOnPress(contact)}
                  />
                </div>
              );
            })
          ) : (
            <EmptyStateButton
              title="No Results"
              description="Check for typos or search for something else"
              icon={
                <Icon
                  icon="ion:search"
                  height={30}
                  style={{ color: Colors.GRAY1 }}
                />
              }
              iconBox={40}
              containerStyles={{ paddingTop: 14 }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SearchBoxContactsDropdown;
